// Centralized static strings/constants for SelfOnboarding module

import type {ProgramFormValues} from '@/components/SelfOnboarding/ProgramDashboardV2/helpers/types'

type KPIFieldName = keyof ProgramFormValues

// Percentage input rule: allow a number with at most two decimal places
// (or a partial entry while typing, e.g. "50" / "50." / "50.5").
export const TWO_DECIMAL_PATTERN = /^\d*\.?\d{0,2}$/

// Internal SE Metrics KPI step — percentage fields (left column).
export const INTERNAL_SE_KPI_LEFT_FIELDS: {name: KPIFieldName; label: string; helperText?: string}[] = [
  {name: 'aiAdoption', label: 'AI Adoption', helperText: '%'},
  {name: 'functionalTestAutomation', label: 'Functional Test Automation', helperText: '%'},
  {name: 'overallAutomationCoverage', label: 'Overall Automation Coverage', helperText: '%'},
  {name: 'escapeDefects', label: 'Escape Defects', helperText: '%'},
  {name: 'unitTestCoverage', label: 'Unit Test Coverage', helperText: '%'},
  {name: 'systemAvailability', label: 'System Availability', helperText: '%'},
  {name: 'changeFailureRate', label: 'Change Failure Rate', helperText: '%'},
  {name: 'buildSuccessRate', label: 'Build Success Rate', helperText: '%'},
  {name: 'gtAdoptionRate', label: 'GT Adoption Rate', helperText: '%'},
]

// Internal SE Metrics KPI step — stepper/count fields (right column).
export const INTERNAL_SE_KPI_RIGHT_FIELDS: {name: KPIFieldName; label: string; helperText?: string}[] = [
  {name: 'deploymentFrequency', label: 'Deployment Frequency', helperText: 'count'},
  {name: 'appResponseTimeKpi', label: 'App Response Time', helperText: 'ms'},
  {name: 'leadTimeToChangeKpi', label: 'Lead Time To Change', helperText: 'days'},
  {name: 'meanTimeToResolve', label: 'Mean Time To Resolve', helperText: 'hrs'},
  {name: 'commits', label: 'Commits', helperText: 'count'},
  {name: 'pulls', label: 'Pulls', helperText: 'count'},
  {name: 'totalVulnerability', label: 'Total Vulnerability', helperText: 'count'},
  {name: 'prThroughput', label: 'PR Throughput', helperText: 'count'},
  {name: 'prCycleTime', label: 'PR Cycle Time', helperText: 'hrs'},
]

// Main titles and modal
export const ONBOARDING_TITLES = {
  main: 'Self Onboarding',
  description: 'Please complete the steps below.',
  addTeam: 'Add New Teams Dashboard',
  editTeam: 'Edit Teams Dashboard',
}

// Team Step One (and UniqueTeamStepOne)
export const TEAM_STEP_ONE = {
  teamName: 'Team Name',
  teamNamePlaceholder: 'Enter name',
  ownerName: 'Application owner(s)',
  ownerNamePlaceholder: 'Enter owner(s)',
  ownerEmail: 'Owner’s email address',
  ownerEmailPlaceholder: 'Enter email address',
  teamNameHelper: "Name of the Team's Dashboard. Must be unique.",
  ownerEmailHelper: 'Email of the individuals separated by comma who will be receiving the bi-weekly reports.',
  ownerNameHelper: 'Name of the individuals separated by comma who will be the recipient of bi-weekly reports.',
}

// LinkDashboardModal
export const LINK_DASHBOARD_MODAL = {
  label: 'Program Dashboard',
  placeholder: 'Search program dashboards...',
  loading: 'Loading...',
}

// MainPopup step labels
export const MAIN_POPUP_STEPS = ['Generic Information', 'Section Details', 'Link Program Dashboard', 'Preview']

// ReviewStep
export const REVIEW_STEP_LABELS = {
  linkedProgram: 'Linked Program Dashboard',
}

// FeatureWidget
export const FEATURE_WIDGET = {
  adoBoardName: 'ADO Board Name',
  adoBoardNameHelperText: "Name of the team's ADO board that needs to be monitored.",
  onTimeDelivery: 'On Time Delivery (%)',
  onTimeDeliveryPlaceholder: 'E.g 50',
  onTimeDeliveryHelperText: 'Expected % of stories to be closed on time before the target date per sprint.',
  commitmentMetHelperText: 'Expected % of stories to be closed vs estimated per sprint.',
  velocityHelperText: 'Expected number of story points to be closed per sprint.',
  defectDensityHelperText: 'Expected number of defects (critical and high) per story point of development per sprint',
  AutomationEfficiencyHelperText: 'Expected percentage of bugs found via automation vs manual testing per sprint.',
  testAutomationHelperText: 'Expected percentage of test cases to be automated per sprint .',
  automationCoverageTargetHelperText:
    'The automation coverage percentage your program aims to achieve per sprint. Shown as the threshold line on the trend chart.',
  overallAutomationCoverageTargetHelperText:
    'The overall automation coverage percentage your program aims to achieve per sprint. Shown as the threshold line on the trend chart.',
  programTagsHelperText:
    'Name of the tags (Semicolon separated) to identify a particular pod/program and filter out stories from other pods/program (optional - to be configured in scenarios where multiple pods/programs share the same ADO board)',
  reusabilityTagsHelperText:
    'Name of the tags (Semicolon separated) that is configured on the ADO Board to indicate global stories that is reused across markets.',
  reusabilityBaselineHelperText: 'The month and year till the historical baseline value should be considered.',
  globalStoryPointHelperText:
    'Story points for all the global stories (that are common across markets) from the start of the program till the baseline period.',
  totalStoryPointHelperText:
    'Total story points worked upon so far from the start of the program till the baseline period.',
  baselineHelperText: 'The month and year till the historical Test Automation % baseline value should be considered.',
  automationTestHelperText:
    'The total number of test cases automated so far from the start of the program till the baseline period.',
  totalTestCasesHelperText:
    'The total number of test automation cases considered so far from the start of the program till the baseline period.',
}

// BuildWidget
export const BUILD_WIDGET = {
  label: 'Build Pipeline',
  placeholder: 'Search build jobs...',
  helper: 'Select your CI build job',
  durationLabel: 'Build Duration Threshold (mins)',
  durationPlaceholder: 'Enter duration',
  failureLabel: 'Consecutive Failure Threshold',
  failurePlaceholder: 'Enter number',
  required: 'This field is required',
  durationHelper:
    'Threshold based on average (expected) build duration, post which alerts would be sent out to team leads to notify them of any possible issues causing the delay.',
  buildJobHelper: 'Name of the build pipeline that needs to be monitored.',
  pipelineHelper:
    'Multiple build pipelines can be added and monitored through the same Teams Dashboard if required (optional).',
}

// DeployWidget
export const DEPLOY_WIDGET = {
  label: 'Deploy Job',
  placeholder: 'Search deploy jobs...',
  helper: 'Select your CD deploy job',
  prodBranchHelper:
    'Specify branch names or tag patterns separated by semicolon, including Regular Expression, used for production deployments.',
  hotfixBranchHelper:
    'Specify branch names or tag patterns separated by semicolon, including Regular Expression, used for hotfix deployments.',
  prodEnvLabel: 'Production Environment Names',
  prodEnvPlaceholder: 'Eg: prod_scus;prod_Env',
  prodEnvHelper: 'Name of the production environments separated by semicolon.',
  required: 'This field is required',
  addPipeline: 'Add Pipeline',
  pipelineHelper:
    'Multiple build pipelines can be added and monitored through the same Teams Dashboard if required (optional).',
}

// RepoWidget
export const REPO_WIDGET = {
  label: 'Repository URL',
  placeholder: 'Enter repo URL',
  helper: 'Provide the full repository URL',
  error: 'Repository URL is required',
  info: 'URL of the code repository to be monitored.',
  branchLabel: 'Branch',
  branchPlaceholder: 'Enter branch name',
  branchError: 'Branch is required',
  branchHelperText: 'Branch name of the code repository that needs to be monitored for pull requests and commits.',
  addBranch: 'Add Branch',
  addBranchHelper:
    'Multiple branch names can be monitored through the same widget in the same Teams Dashboard if required (optional).',
  addRepo: 'Add Repository',
}

// AppSecWidget
export const APPSEC_WIDGET = {
  label: 'Repo URL',
  placeholder: 'Enter AppSec repo URL',
  helper: 'Provide full repository URL for AppSec Flow security scans.',
  error: 'Failed to load suggestions',
  urlHelper: 'Provide the full repo URL used for AppSec Flow security scans.',
}

// QAMetricsWidgetFFF
export const QA_WIDGET = {
  label: 'ADO Board',
  placeholder: 'Search QA boards...',
  helper: 'Select your QA board',
  boardHelper: 'Name of the Automation test pipeline in ADO',
  addQABoard: 'Add QA Board',
  error: 'QA board is required',
  helperText: 'Path to the program in ADO.',
  buildPipelineLabel: 'Automation Test Pipeline',
}

// SecurityWidget
export const SECURITY_WIDGET = {
  label: 'Sonar Analysis Branch Details',
  placeholder: 'Enter branch name',
  helper: 'Provide the Sonar branch name if branch-level tracking is required.',
  staticAnalysisLabel: 'Static Analysis Project',
  staticAnalysisPlaceholder: 'Search Sonar Project',
  staticAnalysisHelper: 'Sonar project ID for unit test coverage',
  sonarProjectIdLabel: 'Sonar project ID for unit test coverage',
  error: 'Static analysis project is required',
}

// General
export const GENERAL = {
  addSection: 'Add Section',
  removeSection: 'Remove section',
  loading: 'Loading...',
  continue: 'Continue',
  finish: 'Finish',
  cancel: 'Cancel',
  update: 'Update',
  clear: 'Clear',
}

export const PROGRAM_INFO = {
  programNameHelperText: "Name of the Program's Dashboard. Must be unique.",
  applicationOwnerHelperText: 'Name of the individual who will be the recipient of monthly reports.',
  ownerEmailHelperText: 'Email of the individual who will be receiving the monthly reports.',
  ownerDeptHelperText: 'Department of the owner.',
  appDResponseHelperText: 'Enter a query configured in App Dynamics to track response time metrics accurately.',
  otelResponseHelperText: 'Enter a query configured in ELK to track response time metrics accurately.',
  availabilityQueryHelperText: "Enter a query to monitor and track your program's uptime and reliability",
  storyPointHelperText:
    'Define how many hours are equivalent to one Story Point. This will be used to calculate productivity gains in hours across the dashboards.',
  costTagHelperText:
    "Tag name of the application in Flexera and Kubecost to get infrastructure cost of the program's Azure resources.",
  workItemHelperText:
    'Configuration to indicate whether Lead Time To Change KPI should be tracked per Epic, Feature or User Story.',
  uatHelperText: "Work item State which marks development complete and start of UAT. Ex: 'Closed' or 'Completed'",
  storyPoints: 'Story Point to Hour Conversion',
  departmentLeaderHelperText: 'Select the L1 leader under whose org this program sits.',
  appOwnerHelperText:
    "Select the individual accountable for this application. List is scoped to the selected L1 Leader's org.",
}
